import { useParams } from '@dreamstack/feature-components/src/hooks/useParams'
import { useFormatNumber } from '@dreamstack/i18n'
import mapValues from 'lodash/mapValues'
import type { FunctionComponent } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { BuyToLetGraph } from '../BuyToLetGraph'
import { BTLCalcBlock1 } from './BTLCalcBlock1'
import { BTLCalcBlock2 } from './BTLCalcBlock2'
import { BTLCalcBlock3 } from './BTLCalcBlock3'
import { BTLCalcBlock4 } from './BTLCalcBlock4'
import type { BTLCalcResultsData } from './BTLCalcUtil'
import {
  formatOptionsDecimal1,
  formatOptionsDecimal2,
  useGetChartData,
  useGetNetZeroYear,
  useGetNetZeroYearHF,
  useGetTableData,
} from './BTLCalcUtil'

type BTLCalcResultsPageParams<T> = {
  priceValue: T
  rentValue: T
  notaryFees: T
  putDownValue: T
  agentFees: T
  propertyTax: T
  interestRate: T
  appreciateValue: T
  rentalIncrease: T
  repaymentRate: T
  maintenanceValue: T
  rent: T
}

export const BTLCalcResultsPage: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const urlParams = useParams() as BTLCalcResultsPageParams<string>
  const format = useFormatNumber(formatOptionsDecimal1)
  const format2 = useFormatNumber(formatOptionsDecimal2)

  const urlParamsParsed: BTLCalcResultsPageParams<number> = mapValues(
    urlParams,
    (param) => {
      return parseFloat(param)
    }
  )

  const {
    priceValue,
    rentValue,
    putDownValue,
    propertyTax,
    notaryFees,
    agentFees,
    interestRate,
    appreciateValue,
    repaymentRate,
    maintenanceValue,
  } = urlParamsParsed

  const rentalReturn = (100 * rentValue * 12) / priceValue

  const allFees = notaryFees + agentFees + propertyTax
  const purchaseCosts = (priceValue * allFees) / 100
  const loanSize = priceValue + purchaseCosts - putDownValue
  const mortgagePayment = (loanSize * (interestRate + repaymentRate)) / 100

  const minimumDownpayment = (priceValue * (30 + allFees)) / 100
  const maintenanceCosts = (100 * 12 * maintenanceValue) / priceValue

  const profitabilityPart1 = rentalReturn - maintenanceCosts - interestRate
  const profitabilityPart2 = appreciateValue
  const profitabilityPart3 = -allFees / 15
  const profitability =
    profitabilityPart1 + profitabilityPart2 + profitabilityPart3

  const params = {
    loanSize,
    purchaseCosts,
    mortgagePayment,
    maintenanceCosts,
  }

  const tableData = useGetTableData(
    urlParamsParsed as BTLCalcResultsData,
    params
  )
  const chartData = useGetChartData(
    urlParamsParsed as BTLCalcResultsData,
    params
  )

  const yearNetZero = useGetNetZeroYear(
    urlParamsParsed as BTLCalcResultsData,
    params
  )

  // Use Hypofriend Formular to get yearNetZero Value
  const yearNetZeroHF = useGetNetZeroYearHF(
    urlParamsParsed as BTLCalcResultsData,
    params
  )

  return (
    <div className="flex flex-col-reverse md:flex-row">
      <div className="flex-1 pr-px-48">
        <div className="border-t-2 w-full mb-px-24 border-[rgba(43, 45, 54, 0.2)]" />

        <BTLCalcBlock1
          priceValue={priceValue}
          rentValue={rentValue}
          rentalReturn={rentalReturn}
        />

        <BTLCalcBlock2
          putDownValue={putDownValue}
          propertyTax={propertyTax}
          notaryFees={notaryFees}
          agentFees={agentFees}
          interestRate={interestRate}
          repaymentRate={repaymentRate}
          purchaseCosts={purchaseCosts}
          mortgagePayment={mortgagePayment}
          loanSize={loanSize}
          minimumDownpayment={minimumDownpayment}
        />

        <BTLCalcBlock3
          {...urlParamsParsed}
          maintenanceCosts={maintenanceCosts}
        />
      </div>

      <div className="flex flex-col justify-start flex-1 bg-[rgba(255, 255, 255, 0.5)]">
        <div className="bg-accentroWhite-full p-px-24">
          <div className="flex-grow-0 flex-shrink text-[20px] font-semibold pb-px-12">
            Profitability
          </div>

          <div className="flex flex-row flex-1 flex-grow-0 flex-shrink">
            <div className="flex-1 text-[16px] pr-px-12">
              <span className="text-[13px] font-semibold">
                Your purchase is profitable after
              </span>

              <div className="text-[40px] text-[#73c49f] font-semibold">
                {format2(yearNetZeroHF)} Years
              </div>
            </div>
            <div className="flex-1 text-[16px] pl-px-12">
              <span className="text-[13px] font-semibold">
                Initial net annual gain of investment
              </span>
              <div className="text-[40px] text-[#73c49f] font-semibold">
                {format(profitability) + '%'}
              </div>
            </div>
          </div>
          <div className="flex-1 flex-grow-0 flex-shrink my-px-24 text-[20px] font-semibold">
            Cumulative Profit / Net Worth Result of investment
          </div>

          <div>
            <div className="h-[438px] flex-auto">
              <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                  <div
                    style={{
                      width: width,
                      height: height,
                    }}
                  >
                    <BuyToLetGraph data={chartData} />
                  </div>
                )}
              </AutoSizer>
            </div>
          </div>
        </div>

        <BTLCalcBlock4 tableData={tableData} {...urlParamsParsed} />
      </div>
    </div>
  )
}
